/**
 * Specification Library Page
 *
 * Main page for PowerSpec V3 - Spec-Driven Development.
 * Provides a unified interface for browsing, viewing, and editing specifications.
 *
 * Features:
 * - Specification list with filtering and search
 * - Detail view with TipTap content
 * - Full editor for creating and editing specs
 * - Template selection for new specs
 * - Curator workflow integration
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Button, Modal, Select, TextInput, Spinner, Alert, Breadcrumb } from 'flowbite-react';
import {
  HiPlus,
  HiHome,
  HiDocumentText,
  HiTemplate,
  HiArrowLeft
} from 'react-icons/hi';
import { SpecList } from '../components/SpecList';
import { SpecViewer } from '../components/SpecViewer';
import { SpecEditor } from '../components/SpecEditor';
import {
  getSpecification,
  createSpecification,
  getSpecificationTemplates,
  type Specification,
  type SpecificationTemplate,
  type CreateSpecificationRequest
} from '../api/specifications';
import type { Specification as SpecListSpecification } from '../components/SpecList';
import { useAuth } from '../contexts/AuthContext';

type ViewMode = 'list' | 'view' | 'edit' | 'create';

const SpecLibraryPage: React.FC = () => {
  const { specId } = useParams<{ specId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, selectedClientId } = useAuth();
  const clientId = selectedClientId;

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedSpec, setSelectedSpec] = useState<Specification | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [templates, setTemplates] = useState<SpecificationTemplate[]>([]);
  const [templatesByCategory, setTemplatesByCategory] = useState<Record<string, SpecificationTemplate[]>>({});
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [newSpecTitle, setNewSpecTitle] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [creating, setCreating] = useState(false);

  const fetchSpec = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await getSpecification(id, true);

      if (response.success && response.data?.Records?.[0]) {
        // The API returns APIResponse<{ Records: Specification[] }>
        // So response.data.Records[0] contains { Records: Specification[] }
        const dataWrapper = response.data.Records[0] as unknown as { Records: Specification[] };
        if (dataWrapper.Records?.[0]) {
          setSelectedSpec(dataWrapper.Records[0]);
        } else {
          throw new Error('Specification not found');
        }
      } else {
        throw new Error(response.message || 'Specification not found');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load specification';
      setError(errorMessage);
      console.error('[SpecLibraryPage] Error fetching spec:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Determine view mode from URL
  useEffect(() => {
    const mode = searchParams.get('mode');

    if (specId) {
      if (mode === 'edit') {
        setViewMode('edit');
        fetchSpec(specId);
      } else {
        setViewMode('view');
        fetchSpec(specId);
      }
    } else if (mode === 'create') {
      setViewMode('create');
      setShowCreateModal(true);
    } else {
      setViewMode('list');
      setSelectedSpec(null);
    }
  }, [specId, searchParams, fetchSpec]);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoadingTemplates(true);

      const response = await getSpecificationTemplates({ include_structure: true });

      if (response.success && response.data?.Records?.[0]) {
        // The API returns APIResponse<{ Records: SpecificationTemplate[]; by_category: ... }>
        // So response.data.Records[0] contains the data object with Records and by_category
        const dataWrapper = response.data.Records[0] as unknown as {
          Records: SpecificationTemplate[];
          by_category: Record<string, SpecificationTemplate[]>;
        };
        setTemplates(dataWrapper.Records || []);
        setTemplatesByCategory(dataWrapper.by_category || {});
      }
    } catch (err) {
      console.error('[SpecLibraryPage] Error fetching templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  const handleSpecSelect = (spec: SpecListSpecification) => {
    // SpecList passes its own Specification type, which is compatible for navigation
    navigate(`/specs/${spec.id}`);
  };

  const handleEditSpec = () => {
    if (selectedSpec) {
      setSearchParams({ mode: 'edit' });
      setViewMode('edit');
    }
  };

  const handleViewSpec = () => {
    setSearchParams({});
    setViewMode('view');
  };

  const handleBackToList = () => {
    navigate('/specs');
    setSelectedSpec(null);
    setViewMode('list');
  };

  const handleCreateClick = () => {
    fetchTemplates();
    setShowCreateModal(true);
  };

  const handleCreateSpec = async () => {
    if (!newSpecTitle.trim() || !clientId) {
      return;
    }

    try {
      setCreating(true);
      setError(null);

      const createData: CreateSpecificationRequest = {
        Client_ID: clientId,
        title: newSpecTitle.trim(),
        template_id: selectedTemplateId || undefined
      };

      const response = await createSpecification(createData);

      if (response.success && response.data?.Records?.[0]) {
        // The API returns APIResponse<{ Records: Specification[] }>
        // So response.data.Records[0] contains { Records: Specification[] }
        const dataWrapper = response.data.Records[0] as unknown as { Records: Specification[] };
        if (dataWrapper.Records?.[0]) {
          const newSpec = dataWrapper.Records[0];
          setShowCreateModal(false);
          setNewSpecTitle('');
          setSelectedTemplateId('');
          navigate(`/specs/${newSpec.id}?mode=edit`);
        } else {
          throw new Error('Failed to create specification');
        }
      } else {
        throw new Error(response.message || 'Failed to create specification');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create specification';
      setError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleSpecSaved = (updatedSpec: Specification) => {
    setSelectedSpec(updatedSpec);
  };

  const handleStatusChange = (newStatus: Specification['status']) => {
    if (selectedSpec) {
      setSelectedSpec({ ...selectedSpec, status: newStatus });
    }
  };

  const renderBreadcrumb = () => (
    <Breadcrumb className="mb-4">
      <Breadcrumb.Item href="/dashboard" icon={HiHome}>
        Dashboard
      </Breadcrumb.Item>
      <Breadcrumb.Item href="/specs" icon={HiDocumentText}>
        Specifications
      </Breadcrumb.Item>
      {selectedSpec && (
        <Breadcrumb.Item>
          {selectedSpec.title}
        </Breadcrumb.Item>
      )}
    </Breadcrumb>
  );

  // Loading state
  if (loading && !selectedSpec) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {renderBreadcrumb()}

      {/* Error Alert */}
      {error && (
        <Alert color="failure" className="mb-4" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Specification Library</h1>
              <p className="text-gray-600">Manage your project specifications</p>
            </div>
            <Button color="blue" onClick={handleCreateClick}>
              <HiPlus className="w-5 h-5 mr-2" />
              New Specification
            </Button>
          </div>

          <SpecList
            clientId={clientId || ''}
            onViewSpec={handleSpecSelect}
          />
        </div>
      )}

      {/* View Mode */}
      {viewMode === 'view' && selectedSpec && (
        <div>
          <div className="flex items-center gap-4 mb-4">
            <Button color="light" size="sm" onClick={handleBackToList}>
              <HiArrowLeft className="w-4 h-4 mr-2" />
              Back to List
            </Button>
          </div>

          <SpecViewer
            specId={selectedSpec.id}
            onEdit={handleEditSpec}
            onRefresh={() => fetchSpec(selectedSpec.id)}
          />
        </div>
      )}

      {/* Edit Mode */}
      {viewMode === 'edit' && selectedSpec && (
        <div>
          <div className="flex items-center gap-4 mb-4">
            <Button color="light" size="sm" onClick={handleViewSpec}>
              <HiArrowLeft className="w-4 h-4 mr-2" />
              Back to View
            </Button>
          </div>

          <SpecEditor
            spec={selectedSpec}
            onSave={handleSpecSaved}
            onCancel={handleViewSpec}
            onStatusChange={handleStatusChange}
            autoSave={true}
          />
        </div>
      )}

      {/* Create Specification Modal */}
      <Modal show={showCreateModal} onClose={() => setShowCreateModal(false)} size="lg">
        <Modal.Header>
          <div className="flex items-center gap-2">
            <HiDocumentText className="w-5 h-5 text-blue-500" />
            Create New Specification
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <TextInput
                value={newSpecTitle}
                onChange={(e) => setNewSpecTitle(e.target.value)}
                placeholder="Enter specification title..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template (optional)
              </label>
              {loadingTemplates ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Spinner size="sm" />
                  Loading templates...
                </div>
              ) : (
                <Select
                  value={selectedTemplateId}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                >
                  <option value="">No template (blank spec)</option>
                  {Object.entries(templatesByCategory).map(([category, categoryTemplates]) => (
                    <optgroup key={category} label={category}>
                      {categoryTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Select>
              )}
            </div>

            {selectedTemplateId && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <HiTemplate className="w-4 h-4" />
                  <span>
                    {templates.find(t => t.id === selectedTemplateId)?.description || 'Template selected'}
                  </span>
                </div>
              </div>
            )}

            <div className="pt-2 border-t">
              <button
                type="button"
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                onClick={() => {
                  setShowCreateModal(false);
                  navigate('/spec-templates');
                }}
              >
                Or create your own template &rarr;
              </button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end gap-2">
            <Button color="gray" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              color="blue"
              onClick={handleCreateSpec}
              disabled={!newSpecTitle.trim() || creating}
            >
              {creating ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <HiPlus className="w-4 h-4 mr-2" />
                  Create Specification
                </>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SpecLibraryPage;
